/**
 * Turning a move into a chord, and a pair of chords into facts.
 *
 * Everything in this file derives from `toCanonical` / `respell` / `spellPitchClass` —
 * the chord model does the music, this file only chooses the arguments. Nothing here
 * writes a pitch class by hand, which is the same discipline the fallback table was
 * already under and the reason a move library can be trusted in twelve keys after being
 * checked in one.
 */

import {
  chordDegreeSemitones,
  chordToneSpellings,
  respell,
  spellDegreeStack,
  toCanonical,
  toSpec,
} from '../chord/canonical.js';
import {
  degreeLetter,
  keyCenterLof,
  lineOfFifths,
  mod12,
  noteName,
  normalizeAccidentalGlyphs,
  parseNoteName,
  rankSpellings,
  spellOnLetter,
  spelledNoteToPc,
  spellPitchClass,
  type KeyContext,
} from '../chord/enharmonic.js';
import { normalizePcSet } from '../chord/pcset.js';
import type {
  Alteration,
  CanonicalChord,
  ChordSpec,
  Extension,
  PitchClass,
  SeventhType,
  SpelledNote,
  TriadQuality,
} from '../chord/types.js';
import type { BranchCurrentChord } from '../llm/schemas.js';
import type { KeyEstimate } from '../session/types.js';
import type { CurrentQuality, HeuristicMove, HeuristicPathEntry, PathChord } from './types.js';

/**
 * Read the chord under the player's hands as one of six qualities.
 *
 * The recogniser hands over a pitch-class set and a root, never a quality (rule 1), so
 * this is the cheapest reading of a set that is right often enough to choose between
 * "the relative" and "the parallel". The order of the tests is the whole of the logic:
 * a dominant seventh is checked before plain major because it *is* a plain major with a
 * seventh, and half the library's predicates turn on telling those two apart.
 */
export function readCurrentQuality(current: BranchCurrentChord): CurrentQuality {
  const pcs = new Set(current.pitchClasses);
  const has = (semitones: number): boolean => pcs.has(mod12(current.rootPc + semitones));

  const minorThird = has(3);
  const majorThird = has(4);
  const perfectFifth = has(7);

  if (majorThird && has(10)) return 'dominant';
  if (minorThird && has(6) && !perfectFifth) return 'diminished';
  if (majorThird && has(8) && !perfectFifth) return 'augmented';
  if (minorThird && !majorThird) return 'minor';
  if (majorThird) return 'major';
  if (has(2) || has(5)) return 'suspended';
  // A bare root and fifth, or something the recogniser is unsure about. Major is the
  // reading the old fallback table used and it is the least surprising default.
  return 'major';
}

/**
 * Major or minor, for the moves that only need to know which way the third points.
 *
 * Kept as its own function — and its own name — because the server's fallback table has
 * always exported `currentMode` and its tests call it directly.
 */
export function currentMode(current: BranchCurrentChord): 'major' | 'minor' {
  return readCurrentQuality(current) === 'minor' ? 'minor' : 'major';
}

/** The triad quality and seventh a `quality: 'same'` move copies across. */
const SAME_SHAPE: Readonly<
  Record<CurrentQuality, { quality: TriadQuality; seventh?: SeventhType }>
> = Object.freeze({
  major: { quality: 'major' },
  minor: { quality: 'minor' },
  dominant: { quality: 'major', seventh: 'min7' },
  diminished: { quality: 'diminished' },
  augmented: { quality: 'augmented' },
  suspended: { quality: 'sus4' },
});

/**
 * The seventh actually sounding above the current root, if there is one.
 *
 * Only planing asks. "The same shape moved bodily" is a claim about intervals, and a
 * minor seventh planed up a tone that arrived as a plain triad would make the move's own
 * explanation false — the shape it kept would be a shape the player was not playing.
 * `CurrentQuality` cannot carry this, because every predicate in the library is written
 * against a six-way reading and a twelve-way one would be unreadable.
 */
function soundingSeventh(current: BranchCurrentChord): SeventhType | undefined {
  const pcs = new Set(current.pitchClasses);
  if (pcs.has(mod12(current.rootPc + 11))) return 'maj7';
  if (pcs.has(mod12(current.rootPc + 10))) return 'min7';
  if (pcs.has(mod12(current.rootPc + 9)) && pcs.has(mod12(current.rootPc + 6))) return 'dim7';
  return undefined;
}

/** Where a move's interval is measured from, given the moment it is being played into. */
export function referencePc(
  move: HeuristicMove,
  current: BranchCurrentChord,
  keyEstimate: KeyEstimate,
): PitchClass {
  return move.from === 'key' ? keyEstimate.tonicPc : current.rootPc;
}

/**
 * Build the chord a move lands on, spelled for the key.
 *
 * Returns `null` when the chord cannot be written as a stack of thirds within double
 * accidentals — `respell` will have picked the best root available, and if even that
 * one is unwritable the move is simply not offered here. Silently dropping a candidate
 * is right: there are seventy-eight others.
 */
export function buildTargetChord(
  move: HeuristicMove,
  current: BranchCurrentChord,
  keyEstimate: KeyEstimate,
  currentQuality: CurrentQuality,
): CanonicalChord | null {
  const rootPc = mod12(referencePc(move, current, keyEstimate) + move.semitones);
  const key: KeyContext = { tonicPc: keyEstimate.tonicPc, mode: keyEstimate.mode };

  const shape = move.quality === 'same' ? SAME_SHAPE[currentQuality] : { quality: move.quality };
  const seventh =
    move.quality === 'same' ? (soundingSeventh(current) ?? shape.seventh) : move.seventh;

  const draft: ChordSpec = {
    root: spellPitchClass(rootPc, key),
    quality: shape.quality,
    extensions: [...(move.extensions ?? [])] as Extension[],
    alterations: [...(move.alterations ?? [])] as Alteration[],
    omissions: [],
  };
  if (seventh) draft.seventh = seventh;
  if (move.bassSemitones !== undefined) {
    draft.bass = spellPitchClass(mod12(rootPc + move.bassSemitones), key);
  }

  // `respell` picks the root spelling that makes the whole stack of thirds writable, so
  // a target in D♭ reads D♭ rather than C♯ with a double-flat third. It also prefers the
  // stack's own spelling for the bass, which is what keeps a slash chord readable. A
  // root-relative move then gets the chance to be spelled as the interval it names.
  const intended = move.from === 'root' ? intervalRoot(current, move.semitones, key) : undefined;
  const canonical = spellOffered(toCanonical(draft), key, intended);
  return chordToneNames(canonical) === null ? null : canonical;
}

/**
 * Letter-steps for each interval a root-relative move can name. The tritone is absent on
 * purpose: an augmented fourth and a diminished fifth are equally good names for it, so
 * the key decides, as it always did.
 */
const INTERVAL_LETTER_STEPS: Readonly<Record<number, number>> = Object.freeze({
  0: 0,
  1: 1,
  2: 1,
  3: 2,
  4: 2,
  5: 3,
  7: 4,
  8: 5,
  9: 5,
  10: 6,
  11: 6,
});

/** The spelled root the player is looking at: the symbol's own letters, where they parse. */
function currentRootSpelling(current: BranchCurrentChord, key: KeyContext): SpelledNote {
  const match = /^[A-G](?:bb|b|##|#|x)?/.exec(normalizeAccidentalGlyphs(current.symbol));
  const parsed = match ? parseNoteName(match[0]) : null;
  if (parsed && spelledNoteToPc(parsed) === current.rootPc) return parsed;
  return spellPitchClass(current.rootPc, key);
}

/**
 * The target root spelled as the interval the card names — a major third above A is C♯,
 * whatever the key signature thinks of sharps — or `undefined` where that needs more than
 * a double accidental, or where the interval has no single name.
 */
function intervalRoot(
  current: BranchCurrentChord,
  semitones: number,
  key: KeyContext,
): SpelledNote | undefined {
  const steps = INTERVAL_LETTER_STEPS[semitones];
  if (steps === undefined) return undefined;
  const from = currentRootSpelling(current, key);
  return (
    spellOnLetter(degreeLetter(from.letter, steps), mod12(current.rootPc + semitones)) ?? undefined
  );
}

/** Every accidental a chord needs to be written, slash bass included. */
function accidentalsIn(chord: CanonicalChord): number {
  const stack = spellDegreeStack(toSpec(chord), chord.root);
  if (!stack) return Number.POSITIVE_INFINITY;
  let count = 0;
  for (const entry of stack) count += Math.abs(entry.note.accidental);
  if (chord.bass && chord.bassPc !== chord.rootPc) count += Math.abs(chord.bass.accidental);
  return count;
}

/**
 * Spell a chord that is being *offered*, given the root it was meant to have.
 *
 * `respell` measures every spelling against the key signature, and a minor key's
 * signature is its relative major's — so from a lone Am, centred on C, it named the
 * leading-note chord A♭m (A♭–C♭–E♭) and the Picardy-side mediant D♭, under a label that
 * said "a major third above". Those are the notes of A minor's raised sixth and seventh
 * and a player writes them with sharps every time. So the intended root wins whenever it
 * costs no more double accidentals than the key's choice and at most one more accidental
 * in all, and sits on the key's side of the line of fifths: C♯–E♯–G♯ (three) over
 * D♭–F–A♭ (two), G♯–B–D♯ (two) over A♭–C♭–E♭ (three), but never D♯–F𝄪–A♯ over E♭–G–B♭,
 * and never F♯m in D♭ major, where G♭–B𝄫–D♭ is what the key writes.
 *
 * Exported because the client respells every suggestion it is handed, and it passes the
 * suggestion's own root as `intended`; this way a card the engine wrote comes out the
 * same on both sides of that seam rather than being re-flattened on arrival.
 */
export function spellOffered(
  chord: CanonicalChord,
  key: KeyContext,
  intended?: SpelledNote,
): CanonicalChord {
  const plain = preferReadable(respell(chord, key), key);
  if (!intended || spelledNoteToPc(intended) !== plain.rootPc) return plain;
  if (intended.letter === plain.root.letter && intended.accidental === plain.root.accidental) {
    return plain;
  }
  // Within a tritone of the tonic on the line of fifths, which is where a key's own
  // raised notes live — A minor's G♯ is five fifths up from A, its Picardy C♯ four — and
  // which an F♯m offered in D♭ major, eleven fifths away, is not.
  const tonic = spellPitchClass(key.tonicPc, key);
  if (Math.abs(lineOfFifths(intended) - lineOfFifths(tonic)) > 6) return plain;
  const alternative = withRoot(plain, intended, key);
  if (!alternative) return plain;
  const fits =
    doubleAccidentalsIn(alternative) <= doubleAccidentalsIn(plain) &&
    accidentalsIn(alternative) <= accidentalsIn(plain) + 1;
  return fits ? alternative : plain;
}

/** How many notes of a chord need a double flat or a double sharp to be written. */
function doubleAccidentalsIn(chord: CanonicalChord): number {
  const stack = spellDegreeStack(toSpec(chord), chord.root);
  if (!stack) return Number.POSITIVE_INFINITY;
  let count = 0;
  for (const entry of stack) if (Math.abs(entry.note.accidental) > 1) count += 1;
  if (chord.bass && Math.abs(chord.bass.accidental) > 1) count += 1;
  return count;
}

/** Rebuild a chord on a different enharmonic root, keeping the bass on a chord tone. */
function withRoot(
  chord: CanonicalChord,
  root: SpelledNote,
  key: KeyContext,
): CanonicalChord | null {
  const spec: ChordSpec = { ...toSpec(chord), root };
  delete spec.bass;
  const degrees = chordDegreeSemitones(spec);
  const stack = spellDegreeStack(spec, root, degrees);
  if (!stack) return null;
  if (chord.bassPc !== chord.rootPc) {
    const fromStack = stack.find((entry) => entry.pitchClass === chord.bassPc);
    spec.bass = fromStack ? fromStack.note : spellPitchClass(chord.bassPc, key);
  }
  return toCanonical(spec);
}

/**
 * How many double accidentals a chord may carry before it is respelled for legibility.
 *
 * One is allowed, because one is often simply correct: the borrowed minor fourth of D♭
 * major *is* G♭m, and it *is* written G♭–B𝄫–D♭. Rewriting it as F♯m would put a sharp
 * chord in a five-flat key, which is a different kind of unreadable, and it would
 * disagree with what `respell` names the same chord everywhere else in the app.
 *
 * Two is the point at which no musician follows the arithmetic: the leading-tone
 * diminished seventh of G minor comes out G♭–B𝄫–D𝄫–F𝄫, and the parallel major seventh
 * in E♭ minor comes out D♯–F𝄪–A♯–C𝄪. Nobody writes either.
 */
const DOUBLE_ACCIDENTAL_BUDGET = 1;

/**
 * Trade a little key-signature loyalty for a chord somebody can read.
 *
 * `respell`'s policy weights distance from the key centre against accidental count, and
 * consults the cost of the whole third-stack only as a late tie-break — which is right
 * for the chord a player just *played*, where the letters they were thinking in matter.
 * It is wrong past a point for a chord being *offered*: a branch card is read at a music
 * desk at arm's length, and four flats-of-flats on it is not a suggestion, it is a
 * puzzle. So past the budget above, the enharmonic root that can be written plainly wins.
 *
 * This is the speller's own rule 6 with a softer threshold — it rejects "cannot be
 * written at all", this rejects "cannot be written without a pile of double flats" — and
 * it is applied here rather than in `respell` because `respell` is what names the chord
 * under the player's hands, and that one must keep answering to the key.
 */
export function preferReadable(chord: CanonicalChord, key: KeyContext): CanonicalChord {
  if (doubleAccidentalsIn(chord) <= DOUBLE_ACCIDENTAL_BUDGET) return chord;
  for (const root of rankSpellings(chord.rootPc, keyCenterLof(key))) {
    if (root.letter === chord.root.letter && root.accidental === chord.root.accidental) continue;
    const candidate = withRoot(chord, root, key);
    if (candidate && doubleAccidentalsIn(candidate) <= DOUBLE_ACCIDENTAL_BUDGET) return candidate;
  }
  return chord;
}

/**
 * Note names for a chord, low to high, with a slash bass in front of the stack.
 *
 * The same rule the server's validator applies when it repairs an LLM's spellings: a
 * bass note sounds below everything else, and it may not be a chord tone at all. Kept
 * here so the engine emits spellings that already agree with its own pitch classes
 * rather than relying on being repaired.
 */
export function chordToneNames(chord: CanonicalChord): string[] | null {
  const tones = chordToneSpellings(chord);
  if (!tones) return null;
  if (!chord.bass || chord.bassPc === chord.rootPc) return tones;
  const bass = noteName(chord.bass);
  return [bass, ...tones.filter((name) => name !== bass)];
}

/** The scale degrees a key owns, as semitones above its tonic. */
export function keyScaleDegrees(mode: 'major' | 'minor'): readonly number[] {
  // The minor collection includes the raised seventh. A dominant in a minor key is
  // ordinary rather than borrowed, and calling its leading note "outside the key" would
  // make every honest cadence report as chromatic.
  return mode === 'major' ? [0, 2, 4, 5, 7, 9, 11] : [0, 2, 3, 5, 7, 8, 10, 11];
}

/**
 * Which pitch classes of a chord the key does not own.
 *
 * Takes the pitch classes alone rather than a whole `CanonicalChord`, because
 * `direction.ts` asks the same question about a chord in the *path* — which has never
 * been spelled and never will be. One arbiter for "what the key owns" is worth more than
 * a narrower parameter type.
 */
export function notesOutsideKey(
  chord: { pitchClasses: readonly PitchClass[] },
  keyEstimate: KeyEstimate,
): PitchClass[] {
  const degrees = new Set(keyScaleDegrees(keyEstimate.mode));
  return chord.pitchClasses.filter((pc) => !degrees.has(mod12(pc - keyEstimate.tonicPc)));
}

/**
 * The concrete, computed facts about one move actually being made.
 *
 * Every field here is derived from the two chords rather than written down beside the
 * move, because the whole reason for an explanation is that it says something true about
 * *this* pair of chords. A shared-note count taken from a table would be fiction the
 * moment a move was applied from an unexpected quality.
 */
export interface MoveFacts {
  from: BranchCurrentChord;
  to: CanonicalChord;
  keyEstimate: KeyEstimate;
  /** Notes sounding in both chords, named as the target chord names them. */
  sharedNotes: string[];
  /** Notes in the target that the current chord does not have. */
  newNotes: string[];
  /** Target notes the key does not contain. */
  outsideKeyNotes: string[];
  /** Semitones from the current chord's root up to the target's, 0..11. */
  rootInterval: number;
  /**
   * The matched tail's symbols, oldest first and including the current chord, when a
   * pattern authored this move. Absent everywhere else, and `{setupPhrase}` is defined
   * in the absent case — the same discipline every other slot keeps.
   */
  setup?: readonly string[];
}

/**
 * Name every pitch class of a chord the way the chord itself does.
 *
 * The explanation sits under a card that reads "A7", so its notes have to be A, C♯, E and
 * G. Spelling each pitch class independently against the key would say D♭ for the third,
 * which is the same sound and a different chord as far as anybody reading is concerned.
 */
function targetNoteNames(chord: CanonicalChord): Map<PitchClass, string> {
  const names = new Map<PitchClass, string>();
  const stack = spellDegreeStack(toSpec(chord), chord.root);
  if (stack) for (const entry of stack) names.set(entry.pitchClass, noteName(entry.note));
  if (chord.bass) names.set(chord.bassPc, noteName(chord.bass));
  return names;
}

export function computeFacts(
  current: BranchCurrentChord,
  target: CanonicalChord,
  keyEstimate: KeyEstimate,
  setup?: readonly string[],
): MoveFacts {
  const key: KeyContext = { tonicPc: keyEstimate.tonicPc, mode: keyEstimate.mode };
  const currentPcs = new Set(normalizePcSet(current.pitchClasses));
  const shared: PitchClass[] = [];
  const fresh: PitchClass[] = [];
  for (const pc of target.pitchClasses) {
    if (currentPcs.has(pc)) shared.push(pc);
    else fresh.push(pc);
  }
  const spelled = targetNoteNames(target);
  const name = (pc: PitchClass): string => spelled.get(pc) ?? noteName(spellPitchClass(pc, key));

  return {
    from: current,
    to: target,
    keyEstimate,
    sharedNotes: shared.map(name),
    newNotes: fresh.map(name),
    outsideKeyNotes: notesOutsideKey(target, keyEstimate).map(name),
    rootInterval: mod12(target.rootPc - current.rootPc),
    ...(setup && setup.length > 0 ? { setup: [...setup] } : {}),
  };
}

/**
 * Read one entry of the path the way the current chord is read.
 *
 * The whole of the adaptation between the wire's history and the engine's: the quality
 * comes from `readCurrentQuality`, which is the same function that reads the chord under
 * the player's hands, so a ii is a ii by exactly the same test whether it is being played
 * now or was played two chords ago.
 */
export function readPathChord(entry: HeuristicPathEntry): PathChord {
  return {
    symbol: entry.symbol,
    pitchClasses: entry.pitchClasses,
    rootPc: entry.rootPc,
    bassPc: entry.bassPc,
    quality: readCurrentQuality(entry),
  };
}
